import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  TextField,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { Add, Close, ArrowBack, WarningAmber } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import SavingOverlay from "../components/SavingOverlay";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";
import { Toast } from "../components/Toast";
import {
  professionalAvailabilityService,
  AvailabilityRangePayload,
  EntityOpenHour,
} from "../services/professionalAvailabilityService";
import { colors, inputs, pageHeader } from "../theme/designSystem";

// 0=domingo … 6=sábado (mesma convenção do backend).
const WEEKDAY_LABELS = [
  "Domingo",
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
];

interface TimeRange {
  id?: string;
  start: string;
  end: string;
  holidayWork: boolean;
  notes: string;
}

const minutesToHHmm = (minutes: number): string => {
  const hours = Math.floor(minutes / 60)
    .toString()
    .padStart(2, "0");
  const mins = (minutes % 60).toString().padStart(2, "0");
  return `${hours}:${mins}`;
};

const hhmmToMinutes = (value: string): number => {
  const [hours, mins] = value.split(":").map(Number);
  return hours * 60 + mins;
};

const emptyDayRanges = (): TimeRange[][] => Array.from({ length: 7 }, () => []);

// Opções de horário em passos de 30 min (dropdown MUI — padrão do site).
const TIME_STEP_MIN = 30;
const TIME_OPTIONS: string[] = Array.from(
  { length: (24 * 60) / TIME_STEP_MIN },
  (_, i) => minutesToHHmm(i * TIME_STEP_MIN)
);

// Inclui um valor fora do passo (ex.: 10:15 herdado do Bubble) para o Select não ficar vazio.
const timeOptions = (current: string): string[] =>
  current && !TIME_OPTIONS.includes(current)
    ? [...TIME_OPTIONS, current].sort()
    : TIME_OPTIONS;

const NOTES_MAX_LENGTH = 2000;

const ProfessionalAvailability: React.FC = () => {
  const { navigateTo, getAllParams } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  const params = getAllParams();
  const professionalId = params.get("professionalId") || "";
  const professionalName = params.get("professionalName") || "Profissional";
  const especialidade = params.get("especialidade") || "";
  const entityId = params.get("entityId") || user?.entityId || "";
  const entityName = params.get("entityName") || user?.entityName || "Entidade";

  const [dayRanges, setDayRanges] = useState<TimeRange[][]>(emptyDayRanges);
  const [businessHours, setBusinessHours] = useState<EntityOpenHour[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hoursError, setHoursError] = useState<string>("");

  useEffect(() => {
    document.title = `${professionalName} / Disponibilidade`;
    if (!professionalId) return;

    let cancelled = false;
    setLoading(true);
    professionalAvailabilityService
      .get(professionalId)
      .then((data) => {
        if (cancelled) return;
        const grouped = emptyDayRanges();
        data.ranges.forEach((r) => {
          if (r.weekday >= 0 && r.weekday <= 6) {
            grouped[r.weekday].push({
              id: r.id,
              start: minutesToHHmm(r.startMinute),
              end: minutesToHHmm(r.endMinute),
              holidayWork: r.holidayWork,
              notes: r.notes || "",
            });
          }
        });
        grouped.forEach((ranges) =>
          ranges.sort((a, b) => hhmmToMinutes(a.start) - hhmmToMinutes(b.start))
        );
        setDayRanges(grouped);
        setBusinessHours(data.businessHours);
      })
      .catch(() => {
        if (!cancelled) {
          showToast("Não foi possível carregar a disponibilidade.", "error");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [professionalId]);

  const openHoursOf = (weekday: number): EntityOpenHour[] =>
    businessHours
      .filter((h) => h.weekday === weekday)
      .sort((a, b) => a.startMinute - b.startMinute);

  const describeOpenHours = (weekday: number): string => {
    const open = openHoursOf(weekday);
    if (open.length === 0) return "Clínica fechada";
    return `Clínica ${open
      .map((h) => `${minutesToHHmm(h.startMinute)}-${minutesToHHmm(h.endMinute)}`)
      .join(" e ")}`;
  };

  // Faixa fora do funcionamento não bloqueia o salvamento: o cálculo de horários livres já
  // intersecta as duas grades. O aviso existe para o cadastro não passar despercebido.
  const outsideBusinessHours = (weekday: number, range: TimeRange): boolean => {
    const open = openHoursOf(weekday);
    if (open.length === 0) return true;
    const start = hhmmToMinutes(range.start);
    const end = hhmmToMinutes(range.end);
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return false;
    return !open.some((h) => start >= h.startMinute && end <= h.endMinute);
  };

  const addRange = (weekday: number) => {
    const open = openHoursOf(weekday);
    const start = open.length > 0 ? minutesToHHmm(open[0].startMinute) : "08:00";
    const end = open.length > 0 ? minutesToHHmm(open[0].endMinute) : "12:00";
    setDayRanges((prev) =>
      prev.map((ranges, i) =>
        i === weekday
          ? [...ranges, { start, end, holidayWork: false, notes: "" }]
          : ranges
      )
    );
    if (hoursError) setHoursError("");
  };

  const removeRange = (weekday: number, index: number) => {
    setDayRanges((prev) =>
      prev.map((ranges, i) =>
        i === weekday ? ranges.filter((_, j) => j !== index) : ranges
      )
    );
    if (hoursError) setHoursError("");
  };

  const updateRange = (
    weekday: number,
    index: number,
    patch: Partial<TimeRange>
  ) => {
    setDayRanges((prev) =>
      prev.map((ranges, i) =>
        i === weekday
          ? ranges.map((r, j) => (j === index ? { ...r, ...patch } : r))
          : ranges
      )
    );
    if (hoursError) setHoursError("");
  };

  // Ao copiar, as faixas viram novas (sem id) nos dias de destino: são registros distintos.
  const copyMondayTo = (targets: (weekday: number) => boolean) => {
    setDayRanges((prev) => {
      const monday = prev[1];
      return prev.map((ranges, i) =>
        targets(i)
          ? monday.map((r) => ({
              start: r.start,
              end: r.end,
              holidayWork: r.holidayWork,
              notes: r.notes,
            }))
          : ranges
      );
    });
    if (hoursError) setHoursError("");
  };

  const validateHours = (): string => {
    for (let d = 0; d < 7; d++) {
      const ranges = dayRanges[d];
      for (const r of ranges) {
        if (!r.start || !r.end) {
          return `${WEEKDAY_LABELS[d]}: preencha início e fim de todas as faixas.`;
        }
        if (hhmmToMinutes(r.end) <= hhmmToMinutes(r.start)) {
          return `${WEEKDAY_LABELS[d]}: o horário final deve ser maior que o inicial.`;
        }
      }
      const sorted = [...ranges].sort(
        (a, b) => hhmmToMinutes(a.start) - hhmmToMinutes(b.start)
      );
      for (let i = 1; i < sorted.length; i++) {
        if (hhmmToMinutes(sorted[i].start) < hhmmToMinutes(sorted[i - 1].end)) {
          return `${WEEKDAY_LABELS[d]}: as faixas de disponibilidade não podem se sobrepor.`;
        }
      }
    }
    return "";
  };

  const flattenRanges = (): AvailabilityRangePayload[] => {
    const result: AvailabilityRangePayload[] = [];
    dayRanges.forEach((ranges, weekday) => {
      ranges.forEach((r) => {
        result.push({
          id: r.id || null,
          weekday,
          startMinute: hhmmToMinutes(r.start),
          endMinute: hhmmToMinutes(r.end),
          holidayWork: r.holidayWork,
          notes: r.notes.trim() || null,
        });
      });
    });
    return result;
  };

  const totalOutside = dayRanges.reduce(
    (count, ranges, weekday) =>
      count + ranges.filter((r) => outsideBusinessHours(weekday, r)).length,
    0
  );

  const handleSave = async () => {
    if (saving || !professionalId) return;
    const validation = validateHours();
    if (validation) {
      setHoursError(validation);
      return;
    }
    setHoursError("");
    setSaving(true);
    try {
      await professionalAvailabilityService.set(professionalId, flattenRanges());
      showToast("Disponibilidade salva com sucesso.", "success");
      const data = await professionalAvailabilityService.get(professionalId);
      const grouped = emptyDayRanges();
      data.ranges.forEach((r) => {
        if (r.weekday >= 0 && r.weekday <= 6) {
          grouped[r.weekday].push({
            id: r.id,
            start: minutesToHHmm(r.startMinute),
            end: minutesToHHmm(r.endMinute),
            holidayWork: r.holidayWork,
            notes: r.notes || "",
          });
        }
      });
      grouped.forEach((ranges) =>
        ranges.sort((a, b) => hhmmToMinutes(a.start) - hhmmToMinutes(b.start))
      );
      setDayRanges(grouped);
    } catch (error) {
      console.error("Erro ao salvar disponibilidade:", error);
      showToast("Erro ao salvar a disponibilidade. Tente novamente.", "error");
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => navigateTo("entity-users", { entityId, entityName });

  // Sem largura fixa: no cartão do celular o par de horários divide a linha
  const selectSx = {
    flex: "0 0 104px",
    minWidth: 96,
    "& .MuiOutlinedInput-root": {
      height: inputs.default.height,
      "& fieldset": { borderColor: colors.border },
      "&:hover fieldset": { borderColor: colors.border },
      "&.Mui-focused fieldset": { borderColor: colors.primary },
    },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <SavingOverlay open={saving} />
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <div className="c4u-page-header c4u-page-header--in-panel">
            <IconButton
              className="c4u-page-header__back"
              onClick={goBack}
              title="Voltar para Usuários"
              size="small"
            >
              <ArrowBack />
            </IconButton>
            <div className="c4u-page-header__texts">
              <h1 className="c4u-page-header__title">{entityName} / Disponibilidade</h1>
              <p className="c4u-page-header__subtitle">Faixas de horário em que o profissional atende, dia a dia.</p>
            </div>
            <div className="c4u-page-header__actions">
              <FaqButton />
            </div>
          </div>

          <div className="c4u-panel">
            {/* O profissional é o ESCOPO da tela: vem antes da barra. Não vai no título. */}
            <div className="c4u-scope-line">
              <strong>{professionalName}</strong>
              {especialidade ? ` · ${especialidade}` : ""}
            </div>
            {/* Grade fixa de sete dias: não há filtro; a barra fica só com as ações de página. */}
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields" />
              <div className="c4u-filters__actions">
                <Button
                  size="small"
                  onClick={() => copyMondayTo((i) => i >= 2 && i <= 5)}
                  disabled={loading}
                  sx={{ textTransform: "none", color: colors.primary, minHeight: 36 }}
                >
                  Copiar 2ª → ter a sex
                </Button>
                <Button
                  size="small"
                  onClick={() => copyMondayTo(() => true)}
                  disabled={loading}
                  sx={{ textTransform: "none", color: colors.primary, minHeight: 36 }}
                >
                  Copiar 2ª → todos
                </Button>
              </div>
            </div>

            <div className="c4u-info-box">
              Uma ou mais faixas por dia; dia sem faixa é indisponível. A agenda usa a
              disponibilidade dentro do horário de funcionamento da clínica — faixa fora dele
              é avisada, mas pode ser salva.
            </div>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "1rem 0",
              }}
            >
              <CircularProgress size={18} sx={{ color: colors.primary }} />
              <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary }}>
                Carregando disponibilidade...
              </Typography>
            </Box>
          ) : (
            <Box className="admin-plans-list-container"
            sx={{ background: "none", border: "none", borderRadius: 0, boxShadow: "none" }}>
              <Box className="admin-plans-table admin-entities-table">
                <Box
                  className="c4u-table__header">
                  <Box
                    className="admin-plans-header-cell"
                    sx={{ flex: "0 0 190px", textAlign: "left" }}
                  >
                    Dia da semana
                  </Box>
                  <Box
                    className="admin-plans-header-cell"
                    sx={{ flex: "1 1 auto", minWidth: 260, textAlign: "left" }}
                  >
                    Faixas de disponibilidade
                  </Box>
                  <Box
                    className="admin-plans-header-cell"
                    sx={{ flex: "0 0 90px", justifyContent: "flex-end" }}
                  >
                    Ações
                  </Box>
                </Box>

                <Box className="admin-plans-table-body">
                  {WEEKDAY_LABELS.map((label, weekday) => {
                    const ranges = dayRanges[weekday];
                    return (
                      <Box
                        key={weekday}
                        className="c4u-table__row"
                      >
                        <Box
                          className="admin-plans-cell"
                          sx={{ flex: "0 0 190px", textAlign: "left" }}
                          data-label="Dia da semana"
                        >
                          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                            <Typography
                              sx={{ fontWeight: 600, fontSize: "0.9rem", whiteSpace: "nowrap" }}
                            >
                              {label}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: colors.textSecondary,
                                lineHeight: 1.35,
                              }}
                            >
                              {describeOpenHours(weekday)}
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          className="admin-plans-cell"
                          sx={{ flex: "1 1 auto", minWidth: 260 }}
                          data-label="Faixas de disponibilidade"
                        >
                          {/* O empilhamento fica num wrapper interno: `display` na célula
                              venceria o grid rótulo/valor do cartão no celular. */}
                          <Box sx={{ display: "flex", flexDirection: "column", gap: "0.75rem", width: "100%", minWidth: 0 }}>
                          {ranges.length === 0 ? (
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                color: colors.textSecondary,
                                fontStyle: "italic",
                                pt: "0.5rem",
                              }}
                            >
                              Indisponível
                            </Typography>
                          ) : (
                            ranges.map((range, index) => {
                              const outside = outsideBusinessHours(weekday, range);
                              return (
                                <Box
                                  key={range.id || `new-${index}`}
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "0.25rem",
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                    {/* Os campos quebram linha entre si no celular; o X fica fora do grupo
                                        para nunca cair sozinho numa linha abaixo. */}
                                    <Box
                                      sx={{
                                        flex: "1 1 auto",
                                        minWidth: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: "0.5rem",
                                      }}
                                    >
                                    <TextField
                                      select
                                      value={range.start}
                                      onChange={(e) =>
                                        updateRange(weekday, index, {
                                          start: e.target.value,
                                        })
                                      }
                                      inputProps={{
                                        "aria-label": `${label} início da faixa ${index + 1}`,
                                      }}
                                      SelectProps={{
                                        MenuProps: {
                                          PaperProps: { sx: { maxHeight: 320 } },
                                        },
                                      }}
                                      sx={selectSx}
                                    >
                                      {timeOptions(range.start).map((t) => (
                                        <MenuItem key={t} value={t}>
                                          {t}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                    <Typography
                                      sx={{
                                        fontSize: "0.85rem",
                                        color: colors.textSecondary,
                                      }}
                                    >
                                      às
                                    </Typography>
                                    <TextField
                                      select
                                      value={range.end}
                                      onChange={(e) =>
                                        updateRange(weekday, index, {
                                          end: e.target.value,
                                        })
                                      }
                                      inputProps={{
                                        "aria-label": `${label} fim da faixa ${index + 1}`,
                                      }}
                                      SelectProps={{
                                        MenuProps: {
                                          PaperProps: { sx: { maxHeight: 320 } },
                                        },
                                      }}
                                      sx={selectSx}
                                    >
                                      {timeOptions(range.end).map((t) => (
                                        <MenuItem key={t} value={t}>
                                          {t}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={range.holidayWork}
                                          onChange={(e) =>
                                            updateRange(weekday, index, {
                                              holidayWork: e.target.checked,
                                            })
                                          }
                                          sx={{
                                            color: colors.border,
                                            "&.Mui-checked": { color: colors.primary },
                                          }}
                                        />
                                      }
                                      label="Atende feriados"
                                      sx={{
                                        m: 0,
                                        "& .MuiFormControlLabel-label": {
                                          fontSize: "0.8rem",
                                          color: colors.textSecondary,
                                        },
                                      }}
                                    />
                                    <TextField
                                      value={range.notes}
                                      onChange={(e) =>
                                        updateRange(weekday, index, {
                                          notes: e.target.value,
                                        })
                                      }
                                      placeholder="Observações"
                                      inputProps={{
                                        maxLength: NOTES_MAX_LENGTH,
                                        "aria-label": `${label} observações da faixa ${index + 1}`,
                                      }}
                                      sx={{
                                        flex: "1 1 200px",
                                        maxWidth: 360,
                                        minWidth: 0,
                                        "& .MuiOutlinedInput-root": {
                                          height: inputs.default.height,
                                          "& fieldset": { borderColor: colors.border },
                                          "&:hover fieldset": {
                                            borderColor: colors.border,
                                          },
                                          "&.Mui-focused fieldset": {
                                            borderColor: colors.primary,
                                          },
                                        },
                                      }}
                                    />
                                    </Box>
                                    <IconButton
                                      className="c4u-row-action"
                                      size="small"
                                      onClick={() => removeRange(weekday, index)}
                                      aria-label={`Remover faixa ${index + 1} de ${label}`}
                                      sx={{
                                        flexShrink: 0,
                                        backgroundColor: "#dc3545",
                                        color: "white",
                                        "&:hover": { backgroundColor: "#c82333" },
                                      }}
                                    >
                                      <Close sx={{ fontSize: "1rem" }} />
                                    </IconButton>
                                  </Box>
                                  {outside && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.35rem",
                                        color: "#b26a00",
                                      }}
                                    >
                                      <WarningAmber sx={{ fontSize: "1rem" }} />
                                      <Typography sx={{ fontSize: "0.75rem" }}>
                                        Fora do funcionamento da clínica (
                                        {describeOpenHours(weekday).replace(
                                          "Clínica ",
                                          ""
                                        )}
                                        ) — a agenda só oferece o trecho em comum.
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              );
                            })
                          )}
                          </Box>
                        </Box>
                        <Box
                          className="admin-plans-cell admin-plans-actions"
                          sx={{ flex: "0 0 90px", textAlign: "right" }}
                          data-label="Ações"
                        >
                          <Tooltip title="Adicionar faixa">
                            <IconButton
                              className="c4u-row-action"
                              size="small"
                              aria-label={`Adicionar faixa em ${label}`}
                              onClick={() => addRange(weekday)}
                              sx={{
                                backgroundColor: "#28a745",
                                color: "white",
                                "&:hover": { backgroundColor: "#218838" },
                              }}
                            >
                              <Add sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )}

          {hoursError && (
            <Typography
              sx={{ fontSize: "0.8rem", color: "#dc3545", marginTop: "0.5rem" }}
            >
              {hoursError}
            </Typography>
          )}

          {!loading && totalOutside > 0 && (
            <Typography
              sx={{ fontSize: "0.8rem", color: "#b26a00", marginTop: "0.5rem" }}
            >
              {totalOutside === 1
                ? "1 faixa está fora do horário de funcionamento da clínica. É possível salvar assim."
                : `${totalOutside} faixas estão fora do horário de funcionamento da clínica. É possível salvar assim.`}
            </Typography>
          )}

          <Box sx={{ mt: 2, display: "flex", gap: "1rem" }}>
            <Button
              onClick={handleSave}
              disabled={saving || loading}
              variant="contained"
              startIcon={
                saving ? (
                  <CircularProgress size={16} sx={{ color: colors.white }} />
                ) : undefined
              }
              sx={{
                textTransform: "none",
                backgroundColor: colors.primary,
                color: colors.white,
                boxShadow: "none",
                "&:hover": { backgroundColor: "#029AAB", boxShadow: "none" },
                "&.Mui-disabled": {
                  backgroundColor: colors.primary,
                  opacity: 0.7,
                  color: colors.white,
                },
              }}
            >
              {saving ? "Processando..." : "Salvar"}
            </Button>
          </Box>
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Box>
  );
};

export default ProfessionalAvailability;
